// Strict EIP-712 (eth_signTypedData_v4) hashing. Every value must already be in the exact
// form its declared type allows — no truthy bools, no wrapped or truncated integers, no lossy
// hex — so the hash the server checks always matches what the typed data says.

import { keccak_256 } from "@noble/hashes/sha3.js";

type TypeDef = { name: string; type: string }[];
type Types = Record<string, TypeDef>;

/** Bounds on attacker-controlled typed data (permits/orders are a few KB; bulk listings grow to ~100 KB). */
const MAX_JSON_BYTES = 256 * 1024;
const MAX_TYPES = 64;
const MAX_FIELDS = 64;
const MAX_NAME = 256;
/** Total characters across all type/field names and types; encodeType strings are rebuilt from these. */
const MAX_TYPES_CHARS = 16 * 1024;
/**
 * Checked once before hashing; linear to match. Field names are identifiers. Type names may be any
 * string without the characters that would make encodeType ambiguous (whitespace, "(", ")", ",",
 * "[", "]") — dapps use names like "HyperliquidTransaction:UsdSend". Field types add up to 8 array
 * dimensions.
 */
const NAME_RE = /^[A-Za-z_$][\w$]*$/;
const TYPE_NAME_RE = /^[^\s(),[\]]+$/;
/** Struct names may not shadow primitives (encodeType and encodeField would disagree); ethers rejects them too. */
const PRIMITIVE_RE = /^(?:address|bool|string|bytes(?:[1-9]|[12]\d|3[0-2])?|u?int(?:8|16|24|32|40|48|56|64|72|80|88|96|104|112|120|128|136|144|152|160|168|176|184|192|200|208|216|224|232|240|248|256)?)$/;
const TYPE_RE = /^[^\s(),[\]]+(?:\[\d{0,9}\]){0,8}$/;

/** Validate every type name, field name and field type once, so later string work stays linear. */
function validateTypes(types: Record<string, unknown>): Types {
  const names = Object.keys(types);
  if (names.length > MAX_TYPES + 1) throw new Error("EIP-712: too many types");
  const out: Types = Object.create(null);
  let chars = 0;
  for (const name of names) {
    chars += name.length;
    if (name.length > MAX_NAME || !TYPE_NAME_RE.test(name) || PRIMITIVE_RE.test(name)) throw new Error("EIP-712: invalid type name");
    if (name === "EIP712Domain") continue; // implicit — derived from the domain fields present
    const def = types[name];
    if (!Array.isArray(def) || def.length > MAX_FIELDS) throw new Error("EIP-712: invalid type definition");
    for (const f of def) {
      if (!isObject(f) || typeof f.name !== "string" || typeof f.type !== "string" ||
          f.name.length > MAX_NAME || f.type.length > MAX_NAME || !NAME_RE.test(f.name) || !TYPE_RE.test(f.type)) {
        throw new Error("EIP-712: invalid field definition");
      }
      chars += f.name.length + f.type.length;
    }
    out[name] = def as TypeDef;
  }
  if (chars > MAX_TYPES_CHARS) throw new Error("EIP-712: types section too large");
  return out;
}

/** Element type of an array type ("T[2]" → "T"); types are pre-validated so "[" only starts a suffix. */
function arrayElement(type: string): { elem: string; len: string } | null {
  if (!type.endsWith("]")) return null;
  const open = type.lastIndexOf("[");
  return { elem: type.slice(0, open), len: type.slice(open + 1, -1) };
}

/** Base (non-array) type name. */
function baseTypeOf(type: string): string {
  const open = type.indexOf("[");
  return open < 0 ? type : type.slice(0, open);
}

/** Hash of the full typed data: keccak256("\x19\x01" || domainSeparator || hashStruct(message)). */
export function hashTypedData(typedDataJson: string): Uint8Array {
  if (typeof typedDataJson !== "string" || typedDataJson.length > MAX_JSON_BYTES) throw new Error("EIP-712: typed data too large");
  const parsed = JSON.parse(typedDataJson);
  if (!isObject(parsed)) throw new Error("EIP-712: typed data must be an object");
  const { domain, types, message, primaryType } = parsed;
  if (!isObject(domain) || !isObject(types) || !isObject(message)) throw new Error("EIP-712: missing domain, types or message");

  const filteredTypes = validateTypes(types);
  if (Object.keys(filteredTypes).length > MAX_TYPES) throw new Error("EIP-712: too many types");
  typeHashCache.clear();
  const primary = primaryType ?? Object.keys(filteredTypes)[0];
  if (typeof primary !== "string" || !Object.hasOwn(filteredTypes, primary)) throw new Error("EIP-712: unknown primaryType");

  const domainSeparator = hashStruct("EIP712Domain", domain, { EIP712Domain: eip712DomainType(domain) });
  const messageHash = hashStruct(primary, message, filteredTypes);

  const payload = new Uint8Array(2 + 32 + 32);
  payload[0] = 0x19;
  payload[1] = 0x01;
  payload.set(domainSeparator, 2);
  payload.set(messageHash, 34);
  return keccak_256(payload);
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Build the EIP712Domain type array from domain fields present */
function eip712DomainType(domain: Record<string, unknown>): TypeDef {
  const types: TypeDef = [];
  if ("name" in domain) types.push({ name: "name", type: "string" });
  if ("version" in domain) types.push({ name: "version", type: "string" });
  if ("chainId" in domain) types.push({ name: "chainId", type: "uint256" });
  if ("verifyingContract" in domain) types.push({ name: "verifyingContract", type: "address" });
  if ("salt" in domain) types.push({ name: "salt", type: "bytes32" });
  return types;
}

/** EIP-712 encodeType: "TypeName(type1 name1,...)" with referenced types appended alphabetically */
function encodeType(typeName: string, types: Types): string {
  const referenced = new Set<string>();
  const queue = [typeName];
  while (queue.length) {
    const current = queue.shift()!;
    for (const field of types[current] ?? []) {
      const baseType = baseTypeOf(field.type);
      if (Object.hasOwn(types, baseType) && baseType !== typeName && !referenced.has(baseType)) {
        referenced.add(baseType);
        queue.push(baseType);
      }
    }
  }
  const sig = (name: string) => `${name}(${types[name].map((f) => `${f.type} ${f.name}`).join(",")})`;
  return sig(typeName) + [...referenced].sort().map(sig).join("");
}

/** typeHash per type name for the current hashTypedData call (types are fixed within a call). */
const typeHashCache = new Map<Types, Map<string, Uint8Array>>();

function typeHash(typeName: string, types: Types): Uint8Array {
  let perTypes = typeHashCache.get(types);
  if (!perTypes) typeHashCache.set(types, (perTypes = new Map()));
  let h = perTypes.get(typeName);
  if (!h) perTypes.set(typeName, (h = keccak_256(new TextEncoder().encode(encodeType(typeName, types)))));
  return h;
}

/** EIP-712 hashStruct: keccak256(typeHash || encodeData) */
function hashStruct(typeName: string, data: unknown, types: Types): Uint8Array {
  if (!isObject(data)) throw new Error(`EIP-712: ${typeName} value must be an object`);
  const fields = types[typeName];
  const out = new Uint8Array(32 * (fields.length + 1));
  out.set(typeHash(typeName, types), 0);
  fields.forEach((field, i) => out.set(encodeField(field.type, data[field.name], types), 32 * (i + 1)));
  return keccak_256(out);
}

const HEX_BYTES = /^(?:0x)?((?:[0-9a-fA-F]{2})*)$/;

/** Even-length, already validated hex → bytes (no Buffer: this file is also bundled for the web and iOS). */
function hexToU8(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

function hexBytes(value: unknown, what: string): Uint8Array {
  const m = typeof value === "string" ? HEX_BYTES.exec(value) : null;
  if (!m) throw new Error(`EIP-712: ${what} must be hex`);
  return hexToU8(m[1]);
}

/** Integer from a JSON number (safe integer) or a decimal / 0x-hex string. */
function integer(value: unknown, type: string): bigint {
  if (typeof value === "number" && Number.isSafeInteger(value)) return BigInt(value);
  if (typeof value === "string" && /^(-?\d{1,78}|0x[0-9a-fA-F]{1,64})$/.test(value)) return BigInt(value);
  throw new Error(`EIP-712: invalid ${type} value`);
}

function word(n: bigint): Uint8Array {
  return hexToU8(BigInt.asUintN(256, n).toString(16).padStart(64, "0"));
}

/** Encode a single field value as a 32-byte word */
function encodeField(type: string, value: unknown, types: Types): Uint8Array {
  if (type === "string") {
    if (typeof value !== "string") throw new Error("EIP-712: string value must be a string");
    return keccak_256(new TextEncoder().encode(value));
  }
  if (type === "bytes") return keccak_256(hexBytes(value, "bytes"));

  const arr = arrayElement(type);
  if (arr) {
    if (!Array.isArray(value) || (arr.len !== "" && value.length !== Number(arr.len))) {
      throw new Error("EIP-712: array value must have the declared length");
    }
    const flat = new Uint8Array(value.length * 32);
    value.forEach((v, i) => flat.set(encodeField(arr.elem, v, types), i * 32));
    return keccak_256(flat);
  }

  if (Object.hasOwn(types, type)) return hashStruct(type, value, types);

  if (type === "address") {
    if (typeof value !== "string" || !/^0x[0-9a-fA-F]{40}$/.test(value)) throw new Error("EIP-712: invalid address");
    return word(BigInt(value));
  }
  if (type === "bool") {
    if (typeof value !== "boolean") throw new Error("EIP-712: bool value must be true or false");
    return word(value ? 1n : 0n);
  }

  const int = /^(u?)int(\d+)$/.exec(type);
  if (int) {
    const bits = Number(int[2]);
    if (bits < 8 || bits > 256 || bits % 8 !== 0) throw new Error(`EIP-712: unsupported type ${type}`);
    const n = integer(value, type);
    const [min, max] = int[1] ? [0n, 1n << BigInt(bits)] : [-(1n << BigInt(bits - 1)), 1n << BigInt(bits - 1)];
    if (n < min || n >= max) throw new Error(`EIP-712: ${type} value out of range`);
    return word(n); // two's complement for negative ints
  }

  const fixed = /^bytes(\d+)$/.exec(type);
  if (fixed) {
    const size = Number(fixed[1]);
    const bytes = hexBytes(value, type);
    if (size < 1 || size > 32 || bytes.length !== size) throw new Error(`EIP-712: ${type} value must be ${size} bytes`);
    const w = new Uint8Array(32);
    w.set(bytes, 0); // left-aligned
    return w;
  }

  throw new Error(`Unsupported EIP-712 type: ${type.slice(0, 64)}`);
}
